import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Minimize2,
  Maximize2,
  Paperclip,
  Lock,
  Clock,
  Send,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link,
  Image,
  Trash2,
  ChevronDown,
  AlertCircle,
  Users,
  Search,
  BookUser
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { contactsApi, messengerApi } from '../../api/api';
import { cn } from '../../lib/utils';
import ContactsManager from './ContactsManager';
import { useTheme } from '../../context/ThemeContext';

const DOMAIN = '@mypatriotmail.org';

// Email Input Component with domain suffix and contact search
function EmailInput({ 
  value, 
  onChange, 
  placeholder = "username", 
  contacts = [],
  friends = [],
  onSearch,
  showSuggestions = false,
  onSelectSuggestion,
  onFocus,
  onBlur,
  inputRef
}) {
  const [isFocused, setIsFocused] = useState(false);
  
  // Extract username from full email or show as-is
  const getDisplayValue = () => {
    if (value.endsWith(DOMAIN)) {
      return value.replace(DOMAIN, '');
    }
    return value;
  };
  
  // Handle input change - only store the username part
  const handleChange = (e) => {
    let newValue = e.target.value;
    // Remove @ and domain if user tries to type it
    newValue = newValue.replace(/@.*$/, '');
    // Only allow valid username characters
    newValue = newValue.replace(/[^a-zA-Z0-9._-]/g, '');
    onChange(newValue ? newValue + DOMAIN : '');
    
    // Trigger search if typing
    if (newValue.length >= 1 && onSearch) {
      onSearch(newValue);
    }
  };
  
  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };
  
  const handleBlur = () => {
    // Delay to allow click on suggestion
    setTimeout(() => {
      setIsFocused(false);
      onBlur?.();
    }, 200);
  };

  // Combined suggestions from contacts and friends
  const allSuggestions = [...contacts, ...friends.filter(f => 
    !contacts.some(c => c.email === f.email)
  )];

  return (
    <div className="relative flex-1 flex items-center">
      <div className="flex-1 flex items-center bg-[#252540] rounded-lg border border-[#2d2d44] focus-within:border-[#6D4AFF] transition-colors">
        <Input
          ref={inputRef}
          value={getDisplayValue()}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="flex-1 bg-transparent border-none text-white placeholder:text-gray-600 focus-visible:ring-0 pr-0"
        />
        <span className="text-gray-500 text-sm pr-3 select-none">{DOMAIN}</span>
      </div>
      
      {/* Suggestions Dropdown */}
      {showSuggestions && isFocused && allSuggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-[#1a1a2e] border border-[#2d2d44] rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
          <div className="p-2 text-xs text-gray-500 border-b border-[#2d2d44] flex items-center gap-1">
            <Users className="w-3 h-3" />
            Contacts & Friends
          </div>
          {allSuggestions.map((contact, idx) => (
            <button
              key={contact.id || idx}
              onClick={() => onSelectSuggestion?.(contact)}
              className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#2d2d44] transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs">
                  {(contact.name || contact.display_name || contact.email)?.[0]?.toUpperCase() || '?'}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-white truncate">
                  {contact.name || contact.display_name || contact.email?.split('@')[0]}
                </p>
                <p className="text-xs text-gray-500 truncate">{contact.email}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ComposeModal({
  isOpen,
  onClose,
  replyTo,
  forwardEmail,
  onSend,
  onSaveDraft,
  userEmail
}) {
  const { primaryColor } = useTheme();
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [isEncrypted, setIsEncrypted] = useState(true);
  const [isMaximized, setIsMaximized] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [friends, setFriends] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [ccSuggestions, setCcSuggestions] = useState([]);
  const [bccSuggestions, setBccSuggestions] = useState([]);
  const [activeField, setActiveField] = useState(null); // 'to', 'cc', 'bcc'
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [showSubjectWarning, setShowSubjectWarning] = useState(false);
  const [showContactsManager, setShowContactsManager] = useState(false);
  
  const toInputRef = useRef(null);
  const ccInputRef = useRef(null);
  const bccInputRef = useRef(null);

  // Load contacts and friends on mount
  useEffect(() => {
    const loadContactsAndFriends = async () => {
      try {
        const [contactsData, friendsData] = await Promise.all([
          contactsApi.getContacts().catch(() => []),
          messengerApi.getFriends().catch(() => [])
        ]);
        setContacts(contactsData || []);
        setFriends(friendsData || []);
      } catch (err) {
        console.error('Failed to load contacts/friends:', err);
      }
    };
    
    if (isOpen) {
      loadContactsAndFriends();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setError('');
      if (replyTo) {
        setTo(replyTo.from_user);
        setSubject(`Re: ${replyTo.subject}`);
        setBody(`\n\n---\nOn ${new Date(replyTo.created_at).toLocaleDateString()}, ${replyTo.from_name} wrote:\n${replyTo.preview}`);
      } else if (forwardEmail) {
        setTo('');
        setSubject(`Fwd: ${forwardEmail.subject}`);
        setBody(`\n\n---\nForwarded message:\nFrom: ${forwardEmail.from_name} <${forwardEmail.from_user}>\nDate: ${new Date(forwardEmail.created_at).toLocaleDateString()}\nSubject: ${forwardEmail.subject}\n\n${forwardEmail.body}`);
      } else {
        setTo('');
        setSubject('');
        setBody('');
      }
      setCc('');
      setBcc('');
      setShowCc(false);
      setShowBcc(false);
      setAttachments([]);
      setActiveField(null);
    }
  }, [replyTo, forwardEmail, isOpen]);

  // Search contacts/friends by query
  const searchSuggestions = useCallback((query, field) => {
    if (!query || query.length < 1) {
      if (field === 'to') setToSuggestions([]);
      else if (field === 'cc') setCcSuggestions([]);
      else if (field === 'bcc') setBccSuggestions([]);
      return;
    }
    
    const queryLower = query.toLowerCase();
    const allPeople = [
      ...contacts.map(c => ({ ...c, type: 'contact' })),
      ...friends.filter(f => !contacts.some(c => c.email === f.email)).map(f => ({ ...f, type: 'friend' }))
    ];
    
    const filtered = allPeople.filter(person => {
      const name = (person.name || person.display_name || '').toLowerCase();
      const email = (person.email || '').toLowerCase();
      return name.includes(queryLower) || email.includes(queryLower);
    });
    
    if (field === 'to') setToSuggestions(filtered);
    else if (field === 'cc') setCcSuggestions(filtered);
    else if (field === 'bcc') setBccSuggestions(filtered);
  }, [contacts, friends]);

  const handleSelectSuggestion = (contact, field) => {
    const email = contact.email;
    if (field === 'to') {
      setTo(email);
      setToSuggestions([]);
    } else if (field === 'cc') {
      setCc(email);
      setCcSuggestions([]);
    } else if (field === 'bcc') {
      setBcc(email);
      setBccSuggestions([]);
    }
    setActiveField(null);
  };

  const validateEmail = (email) => {
    return email.endsWith('@mypatriotmail.org');
  };

  const handleSend = async () => {
    setError('');
    
    // Validate recipient
    if (!to) {
      setError('Please enter a recipient');
      return;
    }
    
    if (!validateEmail(to)) {
      setError('Only @mypatriotmail.org emails are allowed');
      return;
    }
    
    // Show warning if no subject (but don't block)
    if (!subject.trim() && !showSubjectWarning) {
      setShowSubjectWarning(true);
      return;
    }

    await sendEmail();
  };

  const sendEmail = async () => {
    setSending(true);
    setShowSubjectWarning(false);
    try {
      await onSend({
        to,
        cc,
        bcc,
        subject: subject.trim() || '(No Subject)',
        body,
        encrypted: isEncrypted,
        attachments
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = () => {
    onSaveDraft({
      to,
      cc,
      bcc,
      subject,
      body,
      encrypted: isEncrypted,
      attachments
    });
  };

  const handleFileAttach = async (e) => {
    const files = Array.from(e.target.files);
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB limit
    
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        setError(`File "${file.name}" exceeds 25MB limit`);
        continue;
      }
      
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result.split(',')[1]; // Remove data:...;base64, prefix
        const newAttachment = {
          name: file.name,
          size: file.size < 1024 ? `${file.size} B` : 
                file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(1)} KB` :
                `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
          type: file.type || 'application/octet-stream',
          data: base64Data
        };
        setAttachments(prev => [...prev, newAttachment]);
      };
      reader.onerror = () => {
        setError(`Failed to read file "${file.name}"`);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className={cn(
          "bg-[#1a1a2e] border-[#2d2d44] p-0 gap-0",
          isMaximized ? "max-w-[90vw] w-[90vw] h-[90vh]" : "max-w-2xl"
        )}
      >
        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b border-[#2d2d44] flex flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-white text-base font-medium">
            {replyTo ? 'Reply' : forwardEmail ? 'Forward' : 'New Message'}
          </DialogTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMaximized(!isMaximized)}
              className="h-7 w-7 text-gray-400 hover:text-white hover:bg-[#2d2d44]"
            >
              {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-7 w-7 text-gray-400 hover:text-white hover:bg-[#2d2d44]"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Form */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive" className="mx-4 mt-4 bg-red-500/10 border-red-500/50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Domain Notice & Contacts Button */}
          <div className="mx-4 mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="flex-1 p-2 bg-[#6D4AFF]/10 rounded-lg border border-[#6D4AFF]/30">
              <p className="text-xs text-[#6D4AFF] flex items-center gap-2">
                <Lock className="w-3 h-3 flex-shrink-0" />
                <span className="hidden sm:inline">Internal network only — type username, domain is auto-filled</span>
                <span className="sm:hidden">Type username, domain auto-filled</span>
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowContactsManager(true)}
              className="border-[#2d2d44] text-gray-400 hover:text-white hover:bg-[#252540] flex-shrink-0"
              data-testid="open-contacts-btn"
            >
              <BookUser className="w-4 h-4 mr-1" />
              Contacts
            </Button>
          </div>

          {/* To Field */}
          <div className="relative px-4 py-2 border-b border-[#2d2d44]">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 w-12">To:</span>
              <EmailInput
                value={to}
                onChange={setTo}
                placeholder="username"
                contacts={toSuggestions}
                friends={[]}
                onSearch={(q) => searchSuggestions(q, 'to')}
                showSuggestions={activeField === 'to'}
                onSelectSuggestion={(c) => handleSelectSuggestion(c, 'to')}
                onFocus={() => setActiveField('to')}
                onBlur={() => {}}
                inputRef={toInputRef}
              />
              <div className="flex gap-2 flex-shrink-0">
                {!showCc && (
                  <button
                    onClick={() => setShowCc(true)}
                    className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    Cc
                  </button>
                )}
                {!showBcc && (
                  <button
                    onClick={() => setShowBcc(true)}
                    className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    Bcc
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Cc Field */}
          {showCc && (
            <div className="px-4 py-2 border-b border-[#2d2d44]">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 w-12">Cc:</span>
                <EmailInput
                  value={cc}
                  onChange={setCc}
                  placeholder="username"
                  contacts={ccSuggestions}
                  friends={[]}
                  onSearch={(q) => searchSuggestions(q, 'cc')}
                  showSuggestions={activeField === 'cc'}
                  onSelectSuggestion={(c) => handleSelectSuggestion(c, 'cc')}
                  onFocus={() => setActiveField('cc')}
                  onBlur={() => {}}
                  inputRef={ccInputRef}
                />
                <button
                  onClick={() => {
                    setShowCc(false);
                    setCc('');
                  }}
                  className="text-gray-500 hover:text-gray-300 flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Bcc Field */}
          {showBcc && (
            <div className="px-4 py-2 border-b border-[#2d2d44]">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 w-12">Bcc:</span>
                <EmailInput
                  value={bcc}
                  onChange={setBcc}
                  placeholder="username"
                  contacts={bccSuggestions}
                  friends={[]}
                  onSearch={(q) => searchSuggestions(q, 'bcc')}
                  showSuggestions={activeField === 'bcc'}
                  onSelectSuggestion={(c) => handleSelectSuggestion(c, 'bcc')}
                  onFocus={() => setActiveField('bcc')}
                  onBlur={() => {}}
                  inputRef={bccInputRef}
                />
                <button
                  onClick={() => {
                    setShowBcc(false);
                    setBcc('');
                  }}
                  className="text-gray-500 hover:text-gray-300 flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Subject Field */}
          <div className="px-4 py-2 border-b border-[#2d2d44] flex items-center gap-2">
            <span className="text-sm text-gray-500 w-12">Subject:</span>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="flex-1 bg-transparent border-none text-white placeholder:text-gray-600 focus-visible:ring-0 p-0 h-auto"
            />
          </div>

          {/* Formatting Toolbar - Hidden on mobile */}
          <div className="hidden sm:flex px-4 py-2 border-b border-[#2d2d44] items-center gap-1">
            <TooltipProvider>
              {[
                { icon: Bold, label: 'Bold' },
                { icon: Italic, label: 'Italic' },
                { icon: Underline, label: 'Underline' },
              ].map(({ icon: Icon, label }) => (
                <Tooltip key={label}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-gray-400 hover:text-white hover:bg-[#2d2d44]"
                    >
                      <Icon className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-[#2d2d44] text-white border-[#3d3d54]">
                    {label}
                  </TooltipContent>
                </Tooltip>
              ))}

              <Separator orientation="vertical" className="h-6 bg-[#2d2d44] mx-1" />

              {[
                { icon: List, label: 'Bullet list' },
                { icon: ListOrdered, label: 'Numbered list' },
              ].map(({ icon: Icon, label }) => (
                <Tooltip key={label}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-gray-400 hover:text-white hover:bg-[#2d2d44]"
                    >
                      <Icon className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-[#2d2d44] text-white border-[#3d3d54]">
                    {label}
                  </TooltipContent>
                </Tooltip>
              ))}
            </TooltipProvider>
          </div>

          {/* Body */}
          <div className="flex-1 p-4 overflow-auto">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message..."
              className={cn(
                "w-full bg-transparent border-none text-gray-300 placeholder:text-gray-600 resize-none focus-visible:ring-0",
                isMaximized ? "min-h-[40vh]" : "min-h-[200px]"
              )}
            />

            {/* Attachments */}
            {attachments.length > 0 && (
              <div className="mt-4 space-y-2">
                {attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-2 bg-[#252540] rounded-lg"
                  >
                    <Paperclip className="w-4 h-4 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-300 truncate">{attachment.name}</p>
                      <p className="text-xs text-gray-500">{attachment.size}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAttachment(index)}
                      className="h-7 w-7 text-gray-400 hover:text-red-400"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-[#2d2d44]">
            {/* Mobile: Stack vertically */}
            <div className="flex flex-col gap-3 sm:hidden">
              {/* Send button - prominent at top on mobile */}
              <Button
                onClick={handleSend}
                disabled={!to || sending}
                style={{ backgroundColor: primaryColor }}
                className="w-full hover:opacity-90 text-white h-12 text-base"
                data-testid="send-email-btn"
              >
                <Send className="w-5 h-5 mr-2" />
                {sending ? 'Sending...' : 'Send Message'}
              </Button>
              
              {/* Controls row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Lock className={cn(
                      "w-4 h-4",
                      isEncrypted ? "text-green-500" : "text-gray-500"
                    )} />
                    <Switch
                      id="encryption-mobile"
                      checked={isEncrypted}
                      onCheckedChange={setIsEncrypted}
                      className="data-[state=checked]:bg-green-500"
                    />
                  </div>
                  
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileAttach}
                      className="hidden"
                    />
                    <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white h-9 w-9" asChild>
                      <span>
                        <Paperclip className="w-5 h-5" />
                      </span>
                    </Button>
                  </label>
                </div>
                
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSaveDraft}
                    className="text-gray-400 hover:text-white hover:bg-[#2d2d44] text-xs px-2"
                  >
                    Draft
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="h-9 w-9 text-gray-400 hover:text-red-400 hover:bg-[#2d2d44]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Desktop: Original horizontal layout */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Lock className={cn(
                    "w-4 h-4",
                    isEncrypted ? "text-green-500" : "text-gray-500"
                  )} />
                  <Label className="text-sm text-gray-400 cursor-pointer" htmlFor="encryption">
                    Encrypt
                  </Label>
                  <Switch
                    id="encryption"
                    checked={isEncrypted}
                    onCheckedChange={setIsEncrypted}
                    className="data-[state=checked]:bg-green-500"
                  />
                </div>

                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileAttach}
                    className="hidden"
                  />
                  <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white" asChild>
                    <span>
                      <Paperclip className="w-4 h-4 mr-2" />
                      Attach
                    </span>
                  </Button>
                </label>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSaveDraft}
                  className="text-gray-400 hover:text-white hover:bg-[#2d2d44]"
                >
                  Save draft
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="h-9 w-9 text-gray-400 hover:text-red-400 hover:bg-[#2d2d44]"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button
                  onClick={handleSend}
                  disabled={!to || sending}
                  style={{ backgroundColor: primaryColor }}
                  className="hover:opacity-90 text-white"
                  data-testid="send-email-btn-desktop"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* No Subject Warning Dialog */}
      <Dialog open={showSubjectWarning} onOpenChange={setShowSubjectWarning}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              Send without subject?
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-400 text-sm">
            Your email does not have a subject line. Are you sure you want to send it anyway?
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="ghost"
              onClick={() => setShowSubjectWarning(false)}
              className="text-gray-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={sendEmail}
              style={{ backgroundColor: primaryColor }}
              className="hover:opacity-90 text-white"
            >
              Send Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contacts Manager Modal */}
      <ContactsManager
        isOpen={showContactsManager}
        onClose={() => setShowContactsManager(false)}
        onSelectContact={(contact) => {
          // Set the To field with selected contact's email
          setTo(contact.email);
          setActiveField(null);
        }}
      />
    </Dialog>
  );
}
